/*
 * 标准 Canvas 双流光（搬自「下载中心08262030_Vue2版 / 双流光标准.js」）。
 * 原文件是页面级 IIFE；这里改成导出 install()，由 Vue 组件在 mounted 后调用，
 * 并返回一个 stop()，组件销毁时停掉 rAF 与两个 Observer。
 */
export default function installStandardDualFlow (options = {}) {
  if (typeof document === 'undefined') return function () {}
  if (document.documentElement.dataset.standardDualFlowInstalled === '1') return function () {}
  document.documentElement.dataset.standardDualFlowInstalled = '1'
  var disposed = false

  const style = {
    duration: 6100,
    colorDuration: 11800,
    direction: -1,
    offset: .31,
    trailRatio: .47
  };
  const reducedMotion = !options.animate && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const flows = [];

  const wrapDistance = (distance, perimeter) => {
    const wrapped = distance % perimeter;
    return wrapped < 0 ? wrapped + perimeter : wrapped;
  };

  const pointOnRoundedRect = (distance, width, height, radius) => {
    const inset = .65;
    const cornerRadius = Math.min(radius, (height - inset * 2) / 2);
    const left = inset;
    const top = inset;
    const right = width - inset;
    const bottom = height - inset;
    const horizontal = Math.max(0, right - left - cornerRadius * 2);
    const vertical = Math.max(0, bottom - top - cornerRadius * 2);
    const arc = Math.PI * cornerRadius / 2;
    const perimeter = horizontal * 2 + vertical * 2 + arc * 4;
    let cursor = wrapDistance(distance, perimeter);

    if (cursor <= horizontal) return { x: left + cornerRadius + cursor, y: top, perimeter };
    cursor -= horizontal;
    if (cursor <= arc) { const angle = -Math.PI / 2 + cursor / cornerRadius; return { x: right - cornerRadius + Math.cos(angle) * cornerRadius, y: top + cornerRadius + Math.sin(angle) * cornerRadius, perimeter }; }
    cursor -= arc;
    if (cursor <= vertical) return { x: right, y: top + cornerRadius + cursor, perimeter };
    cursor -= vertical;
    if (cursor <= arc) { const angle = cursor / cornerRadius; return { x: right - cornerRadius + Math.cos(angle) * cornerRadius, y: bottom - cornerRadius + Math.sin(angle) * cornerRadius, perimeter }; }
    cursor -= arc;
    if (cursor <= horizontal) return { x: right - cornerRadius - cursor, y: bottom, perimeter };
    cursor -= horizontal;
    if (cursor <= arc) { const angle = Math.PI / 2 + cursor / cornerRadius; return { x: left + cornerRadius + Math.cos(angle) * cornerRadius, y: bottom - cornerRadius + Math.sin(angle) * cornerRadius, perimeter }; }
    cursor -= arc;
    if (cursor <= vertical) return { x: left, y: bottom - cornerRadius - cursor, perimeter };
    cursor -= vertical;
    const angle = Math.PI + cursor / cornerRadius;
    return { x: left + cornerRadius + Math.cos(angle) * cornerRadius, y: top + cornerRadius + Math.sin(angle) * cornerRadius, perimeter };
  };

  const resizeFlow = (flow, force = false) => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, flow.frame.offsetWidth);
    const height = Math.max(1, flow.frame.offsetHeight);
    const radius = Math.max(1, parseFloat(getComputedStyle(flow.frame).borderTopLeftRadius) || 4);
    const pixelWidth = Math.max(1, Math.round(width * dpr));
    const pixelHeight = Math.max(1, Math.round(height * dpr));

    if (!force && flow.canvas.width === pixelWidth && flow.canvas.height === pixelHeight && flow.radius === radius) {
      flow.width = width;
      flow.height = height;
      return false;
    }

    flow.width = width;
    flow.height = height;
    flow.radius = radius;
    flow.canvas.width = pixelWidth;
    flow.canvas.height = pixelHeight;
    flow.context.setTransform(dpr, 0, 0, dpr, 0, 0);
    return true;
  };

  const drawTrail = (flow, headDistance, baseHue, direction) => {
    const perimeter = pointOnRoundedRect(0, flow.width, flow.height, flow.radius).perimeter;
    const trailLength = perimeter * style.trailRatio;
    const tailStart = headDistance - direction * trailLength;
    const appendRoundedPath = (startDistance, endDistance) => {
      const span = Math.abs(endDistance - startDistance);
      const samples = Math.max(1, Math.ceil(span / 1.35));
      const start = pointOnRoundedRect(startDistance, flow.width, flow.height, flow.radius);
      flow.context.moveTo(start.x, start.y);
      for (let sample = 1; sample <= samples; sample += 1) {
        const distance = startDistance + (endDistance - startDistance) * sample / samples;
        const point = pointOnRoundedRect(distance, flow.width, flow.height, flow.radius);
        flow.context.lineTo(point.x, point.y);
      }
    };

    // 先铺一条很淡的完整底线，保证渐变带的交界处在任何 DPR/缩放下
    // 都没有像素缝隙。整条路径只 stroke 一次，不会产生重复圆帽亮点。
    flow.context.beginPath();
    appendRoundedPath(tailStart, headDistance);
    flow.context.lineCap = 'round';
    flow.context.lineJoin = 'round';
    flow.context.lineWidth = .5;
    flow.context.strokeStyle = `hsla(${baseHue}, 88%, 66%, .12)`;
    flow.context.shadowColor = 'transparent';
    flow.context.shadowBlur = 0;
    flow.context.stroke();

    // 渐隐、增粗和变色仍分带计算，但每一带内部是一条连续折线。
    // 内部接缝使用 butt 端帽精确相接，只在真正的头尾保留圆帽。
    const bands = Math.min(36, Math.max(22, Math.ceil(trailLength / 8)));
    for (let index = 0; index < bands; index += 1) {
      const progress = (index + 1) / bands;
      const previousProgress = index / bands;
      const startDistance = tailStart + direction * previousProgress * trailLength;
      const endDistance = tailStart + direction * progress * trailLength;
      const intensity = Math.pow(progress, 2.35);
      const hue = (baseHue + progress * 38) % 360;
      const saturation = 82 + progress * 14;
      const lightness = 58 + progress * 18;

      flow.context.beginPath();
      appendRoundedPath(startDistance, endDistance);
      flow.context.lineCap = index === 0 || index === bands - 1 ? 'round' : 'butt';
      flow.context.lineJoin = 'round';
      flow.context.lineWidth = .35 + intensity * 1.9;
      flow.context.strokeStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${.08 + intensity * .92})`;
      flow.context.shadowColor = progress > .8 ? `hsla(${hue}, 100%, 72%, ${intensity * .78})` : 'transparent';
      flow.context.shadowBlur = progress > .8 ? 2.2 : 0;
      flow.context.stroke();
    }
  };

  const resizeObserver = new ResizeObserver(entries => {
    entries.forEach(entry => {
      const flow = flows.find(item => item.frame === entry.target);
      if (flow) flow.needsResize = true;
    });
  });

  const addTarget = frame => {
    if(frame && frame.closest('#bean-chat-overlay')){
      // Intro tiles are cloned from the download surface: discard cloned or
      // previously registered canvases before installing the static chat edge.
      frame.querySelectorAll(':scope > .dual-flow-canvas').forEach(canvas=>canvas.remove());
      for(let index=flows.length-1;index>=0;index--){
        if(flows[index].frame===frame){resizeObserver.unobserve(frame);flows.splice(index,1);}
      }
      frame.querySelectorAll(':scope > .flow-frame').forEach(node=>node.style.setProperty('display','none','important'));
      if(!frame.querySelector(':scope > .chat-static-border-edge')){
        frame.classList.add('chat-static-border');
        if(getComputedStyle(frame).position==='static')frame.style.position='relative';
        const edge=document.createElement('span');edge.className='chat-static-border-edge';edge.setAttribute('aria-hidden','true');frame.prepend(edge);
      }
      return;
    }
    if (!frame || frame.querySelector(':scope > .dual-flow-canvas')) return;
    // 原版是直接 remove 掉旧流光层；在 Vue 页面里这些节点归 Vue 管，
    // 外部删除会让下一次 patch 抛 removeChild 错误，所以改成隐藏。
    frame.querySelectorAll(':scope > .flow-frame').forEach(legacyFrame => {
      legacyFrame.style.setProperty('display', 'none', 'important');
    });
    frame.classList.add('standard-dual-flow');
    const canvas = document.createElement('canvas');
    canvas.className = 'dual-flow-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    frame.prepend(canvas);
    const sequence = flows.length;
    const flow = {
      frame,
      canvas,
      context: canvas.getContext('2d'),
      width: 1,
      height: 1,
      radius: 4,
      needsResize: true,
      phaseOffset: (sequence * .1375035237) % 1,
      hueOffset: (sequence * 47) % 360,
      duration: style.duration * (.86 + (sequence % 9) * .035)
    };
    flows.push(flow);
    resizeObserver.observe(frame);
    resizeFlow(flow, true);
  };

  const refreshTargets = () => {
    document.documentElement.classList.add('official-package-standard');
    document.querySelectorAll('.channel-card > em').forEach(label => {
      // 同上：不删节点，只隐藏，避免和 Vue 的 DOM diff 打架。
      if (label.textContent.trim() === '可获取') label.style.setProperty('display', 'none', 'important');
    });
    document.querySelectorAll('.system, .emulator-button, .product-entry-button, .official-site-button, .channel-card, .featured:not(.simulator-dual), .primary-action, .channel-qr-launch, .qr-launch-button, [data-dual-flow]').forEach(addTarget);
  };

  let lastDrawTime = 0;
  const draw = time => {
    if(disposed)return;
    // Canvas 流光以 30fps 更新已经足够连续；CSS 色相、首页球体和流星
    // 仍由各自的动画独立运行。高刷屏下不再重复绘制相同的中间帧。
    if (!reducedMotion && time - lastDrawTime < 32) {
      if (!disposed) requestAnimationFrame(draw);
      return;
    }
    lastDrawTime = time;
    for (let index = flows.length - 1; index >= 0; index -= 1) {
      const flow = flows[index];
      if (!flow.frame.isConnected) {
        resizeObserver.unobserve(flow.frame);
        // A cached card may be reattached later; don't leave an unowned frozen canvas on it.
        flow.canvas.remove();
        flows.splice(index, 1);
        continue;
      }
      // ResizeObserver 已负责尺寸变化。逐帧读取 offsetWidth/offsetHeight
      // 会在绘制前强制同步布局，是完整球体舞台运行时最昂贵的路径之一。
      if (flow.needsResize) {
        resizeFlow(flow);
        flow.needsResize = false;
      }
      if (flow.width <= 1 || flow.height <= 1) continue;
      flow.context.clearRect(0, 0, flow.width, flow.height);
      const perimeter = pointOnRoundedRect(0, flow.width, flow.height, flow.radius).perimeter;
      const rawPhase = reducedMotion ? .16 : (time % flow.duration) / flow.duration;
      const phase = style.offset + flow.phaseOffset + rawPhase * style.direction;
      const colorPhase = (reducedMotion ? 205 : (time % style.colorDuration) / style.colorDuration * 360) + flow.hueOffset;
      const firstHue = colorPhase;
      const secondHue = (colorPhase + 155) % 360;
      drawTrail(flow, phase * perimeter, firstHue, style.direction);
      drawTrail(flow, (phase + .5) * perimeter, secondHue, style.direction);
    }
    if (!reducedMotion && !disposed) requestAnimationFrame(draw);
  };

  let refreshQueued = false;
  const scheduleRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    queueMicrotask(() => {
      refreshQueued = false;
      refreshTargets();
    });
  };

  refreshTargets();
  const mutationRoot = document.body || document.documentElement;
  const mutationObserver = new MutationObserver(scheduleRefresh);
  if (mutationRoot) {
    mutationObserver.observe(mutationRoot, { childList: true, subtree: true });
  }
  requestAnimationFrame(draw);

  return function stop () {
    disposed = true
    mutationObserver.disconnect()
    resizeObserver.disconnect()
    flows.length = 0
    delete document.documentElement.dataset.standardDualFlowInstalled
  }
}



// WEBPACK FOOTER //
// ./src/download/standard-dual-flow.js